import { useEffect, useState } from "react";
import { useI18n, type StringKey } from "../i18n";
import { issueAsset, loadAssetAssignments, loadAssets, loadDirectory, returnAsset, saveAsset, type Asset, type AssetAssignment } from "../data";
import { money } from "../lib/payslip";
import { downloadText, toCsv } from "../lib/csv";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const CATS = ["laptop", "phone", "sim", "tablet", "vehicle", "card", "key", "uniform", "tools", "other"];
const tone: Record<string, string> = { available: "success", assigned: "info", repair: "warning", retired: "neutral", lost: "danger" };

/** Company assets (custody, عُهد): who holds what, hand out and take back, history. */
export function Assets({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const manage = ws.permissions.has("assets.manage");
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [assigns, setAssigns] = useState<AssetAssignment[]>([]);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState<string | null>(null);
  const [act, setAct] = useState({ emp: "", on: iso(new Date()), condition: "good", note: "" });
  const [f, setF] = useState({ tag: "", name: "", category: "laptop", serial: "", value: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = async () => { setAssets(await loadAssets(cid)); setAssigns(await loadAssetAssignments(cid)); };
  useEffect(() => { void refresh(); void loadDirectory(cid).then(setPeople); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const holder = (a: Asset) => assigns.find((x) => x.asset_id === a.id && !x.returned_on);
  const person = (id: string) => people.find((x) => x.employment_id === id);
  const name = (id: string) => { const p = person(id); return p ? pick(p.full_name, p.full_name_ar) : "—"; };
  const shown = (assets ?? []).filter((a) => (status === "all" || a.status === status)
    && (!q || `${a.tag} ${a.name} ${a.serial ?? ""} ${holder(a) ? name(holder(a)!.employment_id) : ""}`.toLowerCase().includes(q.toLowerCase())));
  const leaversHolding = (assets ?? []).filter((a) => { const h = holder(a); return h && person(h.employment_id)?.status === "terminated"; });

  async function run(fn: () => Promise<unknown>, done: string) {
    setMsg(null);
    try { await fn(); await refresh(); setMsg({ ok: true, text: done }); setOpen(null); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }
  const exportCsv = () => downloadText("assets.csv", toCsv(["Tag", "Name", "Category", "Serial", "Value", "Status", "Held by", "Since"],
    (assets ?? []).map((a) => { const h = holder(a); return [a.tag, a.name, a.category, a.serial ?? "", a.value != null ? String(a.value) : "", a.status, h ? name(h.employment_id) : "", h?.issued_on ?? ""]; })));

  return (
    <div className="page">
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {leaversHolding.length > 0 && <p className="alert warning">{t("asLeavers").replace("{n}", String(leaversHolding.length))}: {leaversHolding.map((a) => `${a.tag} (${name(holder(a)!.employment_id)})`).join(", ")}</p>}
      <section className="card">
        <div className="toolbar">
          <input className="search" type="search" placeholder={t("search")} aria-label={t("search")} value={q} onChange={(e) => setQ(e.target.value)} />
          <select aria-label={t("status")} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">{t("alertsAll")}</option>
            {Object.keys(tone).map((s) => <option key={s} value={s}>{t(`asStatus_${s}` as StringKey)}</option>)}
          </select>
          <button type="button" className="btn secondary small" onClick={exportCsv}>CSV</button>
        </div>
        {assets === null ? <p className="muted">{t("loading")}</p> : shown.length === 0 ? <p className="muted">{t("asNone")}</p> : (
          <div className="table-wrap"><table>
            <thead><tr><th>{t("asTag")}</th><th>{t("asItem")}</th><th>{t("asHolder")}</th><th className="hide-sm">{t("asValue")}</th><th>{t("status")}</th><th /></tr></thead>
            <tbody>{shown.map((a) => {
              const h = holder(a);
              return [
                <tr key={a.id}>
                  <td className="mono">{a.tag}</td>
                  <td>{a.name}<br /><span className="muted small-note">{t(`asCat_${a.category}` as StringKey)}{a.serial ? ` · ${a.serial}` : ""}</span></td>
                  <td>{h ? <><button type="button" className="link" onClick={() => onOpen(h.employment_id)}>{name(h.employment_id)}</button><br /><span className="muted small-note">{t("payFrom")} {h.issued_on}</span></> : "—"}</td>
                  <td className="hide-sm mono">{a.value != null ? money(a.value) : "—"}</td>
                  <td><span className={`badge ${tone[a.status]}`}>{t(`asStatus_${a.status}` as StringKey)}</span></td>
                  <td className="cell-actions">{manage && (a.status === "available" || a.status === "assigned") && (
                    <button type="button" className="btn secondary small" aria-expanded={open === a.id} onClick={() => setOpen(open === a.id ? null : a.id)}>{h ? t("asTakeBack") : t("asHandOut")}</button>
                  )}</td>
                </tr>,
                open === a.id && (
                  <tr key={`${a.id}-act`}><td colSpan={6}>
                    <form className="row" onSubmit={(e) => { e.preventDefault(); void run(() => h ? returnAsset(a.id, act.on, act.condition, act.note) : issueAsset(a.id, act.emp, act.on, act.note), h ? t("asReturned") : t("asIssued")); }}>
                      {!h && <select aria-label={t("employee")} value={act.emp} onChange={(e) => setAct({ ...act, emp: e.target.value })} required>
                        <option value="">—</option>
                        {people.filter((p) => p.status !== "terminated").map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)}</option>)}
                      </select>}
                      <input type="date" aria-label={t("date")} value={act.on} onChange={(e) => setAct({ ...act, on: e.target.value })} />
                      {h && <select aria-label={t("asCondition")} value={act.condition} onChange={(e) => setAct({ ...act, condition: e.target.value })}>
                        <option value="good">{t("asGood")}</option><option value="damaged">{t("asDamaged")}</option><option value="lost">{t("asLost")}</option>
                      </select>}
                      <input placeholder={t("note")} aria-label={t("note")} value={act.note} onChange={(e) => setAct({ ...act, note: e.target.value })} />
                      <button className="btn primary small">{h ? t("asTakeBack") : t("asHandOut")}</button>
                    </form>
                  </td></tr>
                ),
              ];
            })}</tbody>
          </table></div>
        )}
      </section>
      {manage && (
        <form className="card" onSubmit={(e) => { e.preventDefault(); void run(() => saveAsset(cid, { tag: f.tag, name: f.name, category: f.category, serial: f.serial || null, value: f.value ? Number(f.value) : null }), t("saved")).then(() => setF({ ...f, tag: "", name: "", serial: "", value: "" })); }}>
          <h2>{t("asNew")}</h2>
          <div className="field-row">
            <label className="field"><span>{t("asTag")}</span><input value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} required placeholder="LT-042" /></label>
            <label className="field"><span>{t("asItem")}</span><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></label>
            <label className="field"><span>{t("otKind")}</span>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{CATS.map((c) => <option key={c} value={c}>{t(`asCat_${c}` as StringKey)}</option>)}</select></label>
            <label className="field"><span>{t("asSerial")}</span><input value={f.serial} onChange={(e) => setF({ ...f, serial: e.target.value })} /></label>
            <label className="field"><span>{t("asValue")}</span><input type="number" min="0" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></label>
          </div>
          <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
        </form>
      )}
    </div>
  );
}

/** Self-service: company items in my custody. */
export function MyAssets({ ws }: { ws: Workspace }) {
  const { t } = useI18n();
  const [rows, setRows] = useState<{ a: Asset; since: string }[]>([]);
  useEffect(() => {
    void Promise.all([loadAssets(ws.membership.company_id), loadAssetAssignments(ws.membership.company_id)]).then(([as, aa]) =>
      setRows(aa.filter((x) => !x.returned_on && x.employment_id === ws.membership.employment_id)
        .map((x) => ({ a: as.find((y) => y.id === x.asset_id)!, since: x.issued_on })).filter((x) => x.a)));
  }, [ws.membership.company_id, ws.membership.employment_id]);
  if (rows.length === 0) return null;
  return (
    <section className="card">
      <h2>{t("myAssets")}</h2>
      <ul className="req-list">
        {rows.map(({ a, since }) => <li key={a.id}><span><strong>{a.name}</strong> <span className="muted small-note">· {a.tag}{a.serial ? ` · ${a.serial}` : ""}</span></span><span className="muted small-note">{t("payFrom")} {since}</span></li>)}
      </ul>
      <p className="muted small-note">{t("myAssetsHelp")}</p>
    </section>
  );
}
